import React, { FC, useEffect, useState } from 'react';

import styles from '../BillingSection.module.scss';
import { ICustomer } from '../../../../model';
import { FormDropDown, requiredValidator, Spinner } from '../../../common';
import { NumericTextBox, Switch, SwitchChangeEvent } from '@progress/kendo-react-inputs';
import { Field, Form, FormElement } from '@progress/kendo-react-form';
import { AxiosError } from 'axios';
import { quickBookExternalService } from '../../../../services/api/quickBookExternal';
import { IQuickBookCustomer } from '../../../../model/api/customers/quickbook-customer';
import { Label } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';
import { usePermissions } from '../../../auth/PermissionsProvider';
import {
  ICustomerExternalBillingRate,
  ICustomerExternalBillingSettings,
  IQuickBookProduct,
} from '../../../../model/api/customers/customer-quickbook-settings';
import { billingService } from '../../../../services/api/billing';
import { IBillingMetric } from '../../../../model/api/customers/billing-metric';
import { FormDropDownWithSearch } from '../../../common/forms/form-dropdown/FormDropdownWithSearch';
import { CheckBoxFormField } from '../../../common/forms/form-checkbox/CheckBoxFormField';
import { trashIcon } from '@progress/kendo-svg-icons';
import { notifications } from '../../../../services';
import { useRevalidator } from 'react-router-dom';
import { BillingMode } from '../../../../model/api/customers/billing-mode';
import { IBillingClass } from '../../../../model/api/customers/billing-class';
import { IBillingDepartment } from '../../../../model/api/customers/billing-department';
import {IBillingTerm} from "../../../../model/api/customers/billing-term";

interface IBillingSectionProps {
  customer: ICustomer;
  hasBillingAccess: boolean;
  billingMode: BillingMode;
}

const createEmptyRate = (): ICustomerExternalBillingRate => ({
  metricId: 0,
  externalBillingSKUId: '',
});

export const BillingSettingsSection: FC<IBillingSectionProps> = ({ customer, hasBillingAccess, billingMode }) => {
  const customerId = customer.id;
  const revalidator = useRevalidator();
  const formRef = React.useRef<any>(null);
  const [edit, setEdit] = useState(false);
  const { hasEditAccess } = usePermissions();
  const [loadingState, setLoadingState] = useState<{ loading: boolean; error: string | null }>({
    loading: false,
    error: null,
  });
  const [billingMetrics, setBillingMetrics] = useState<IBillingMetric[]>([]);
  const [productsSKU, setProductsSKU] = useState<Array<IQuickBookProduct>>([]);
  const [quickBookCustomers, setQuickBookCustomers] = useState<Array<IQuickBookCustomer>>([]);
  const [billingTerms, setBillingTerms] = useState<Array<IBillingTerm>>([]);
  const [billingClasses, setBillingClasses] = useState<Array<IBillingClass>>([]);
  const [billingDepartments, setBillingDepartments] = useState<Array<IBillingDepartment>>([]);
  const [qbSettings, setQbSettings] = useState<ICustomerExternalBillingSettings>({
    integrationEnabled: false,
    externalBillingCustomerId: undefined,
    rates: [],
    invoiceAttachmentsEnabled: false,
    breakdownByLotOwner: false,
  });

  useEffect(() => {
    const billingSettings = billingMode === BillingMode.NexPark ? customer.nexParkBillingSettings : customer.nexResponseBillingSettings;

    const newSettings = billingSettings ?? {
      integrationEnabled: false,
      externalBillingCustomerId: undefined,
      rates: [],
      invoiceAttachmentsEnabled: false,
      breakdownByLotOwner: false,
    };

    setQbSettings(newSettings);
    setEdit(false);

    if (formRef.current) {
      setTimeout(() => {
        formRef.current.onFormReset();
      }, 0);
    }
  }, [customer, billingMode]);

  useEffect(() => {
    if (qbSettings.integrationEnabled && (!qbSettings.rates || qbSettings.rates.length === 0)) {
      setQbSettings({
        ...qbSettings,
        rates: [createEmptyRate(), createEmptyRate()],
      });
    }
  }, [qbSettings.integrationEnabled]);

  useEffect(() => {
    const initData = async () => {
      setLoadingState({ loading: true, error: null });

      try {
        const [metrics, products, customers, classes, departments, terms]: any = await Promise.all([
          billingService.getBillingMetrics(billingMode),
          quickBookExternalService.getProductList(),
          quickBookExternalService.getCustomerList(),
          quickBookExternalService.getBillingClasses(),
          quickBookExternalService.getBillingDepartments(),
          quickBookExternalService.getBillingTerms(),
        ]);

        setBillingMetrics(metrics);
        setProductsSKU(products);
        setQuickBookCustomers(customers);
        setBillingClasses(classes);
        setBillingDepartments(departments);
        setBillingTerms(terms);
      } catch (error) {
        console.error('Data initialization failed:', error);
        setLoadingState((prev) => ({
          ...prev,
          error: 'Failed to load billing settings data',
        }));
      } finally {
        setLoadingState((prev) => ({ ...prev, loading: false }));
      }
    };

    initData();
  }, [hasBillingAccess, billingMode]);

  const handleAddMetric = () => {
    setQbSettings({
      ...qbSettings,
      rates: [...(qbSettings.rates || []), createEmptyRate()],
    });
  };

  const handleRemoveMetric = (index: number, formChange: (name: string, event: any) => void) => {
    const newRates = [...qbSettings.rates];
    newRates.splice(index, 1);
    setQbSettings({ ...qbSettings, rates: newRates });
    formChange('rates', { value: newRates });
  };

  const onSaveClick = (formRenderProps: any) => {
    if (!qbSettings.integrationEnabled) {
      updateBillingSettings(customerId, billingMode, qbSettings);
    } else {
      formRenderProps.onSubmit();
      handleSettingsSave();
    }
  };

  const handleSettingsSave = () => {
    setLoadingState({ loading: false, error: null });

    if (!qbSettings.externalBillingCustomerId || !qbSettings.externalBillingSalesTermId) {
      return;
    }

    const hasIncompleteRates = qbSettings.rates.some((rate) => !rate.metricId || rate.metricId === 0 || !rate.externalBillingSKUId);

    if (hasIncompleteRates) {
      return;
    }

    const sanitizedRates = qbSettings.rates.filter((rate) => rate.metricId !== 0 && rate.externalBillingSKUId !== '');

    const payload = {
      ...qbSettings,
      rates: sanitizedRates,
    };

    const hasDuplicates = new Set(sanitizedRates.map((r) => r.metricId)).size !== sanitizedRates.length;
    if (hasDuplicates) {
      setLoadingState({ loading: false, error: 'Each metric can be used only once.' });
      return;
    }
    updateBillingSettings(customerId, billingMode, payload);
  };

  const updateBillingSettings = (customerId: number, billingMode: BillingMode, payload: ICustomerExternalBillingSettings) => {
    billingService
      .updateCustomerBillingSettings(customerId, billingMode, payload)
      .then(() => {
        setEdit(false);
        notifications.success(`The settings have been successfully updated.`);
        if (revalidator.state === 'idle') {
          revalidator.revalidate();
        }
      })
      .catch((error: AxiosError | any) => {
        console.error('Failed to save QuickBook settings:', error);
        setLoadingState({ loading: false, error: 'Failed to save QuickBook settings' });
      })
      .finally(() => {
        setLoadingState({ loading: false, error: null });
      });
  };

  const handelSettingsCancel = (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setEdit(false);

    const originalSettings = billingMode === BillingMode.NexPark ? customer.nexParkBillingSettings : customer.nexResponseBillingSettings;

    setQbSettings(
      originalSettings ?? {
        integrationEnabled: false,
        externalBillingCustomerId: undefined,
        rates: [],
        invoiceAttachmentsEnabled: false,
        breakdownByLotOwner: false,
      },
    );
  };

  const handleServiceToggle = (isChecked: boolean, formChange: (name: string, event: any) => void) => {
    setQbSettings({ ...qbSettings, integrationEnabled: isChecked });
    formChange('integrationEnabled', { value: isChecked });
  };

  const renderSettingsFields = (onFormChange: (name: string, event: any) => void) => {
    const selectedCustomer = quickBookCustomers.find((c) => c.id === qbSettings.externalBillingCustomerId);
    const selectedClass = billingClasses.find((c) => c.id === qbSettings.externalBillingClassId);
    const selectedDepartment = billingDepartments.find((c) => c.id === qbSettings.externalBillingDepartmentId);
    const selectedSalesTerm = billingTerms.find((c) => c.id === qbSettings.externalBillingSalesTermId);

    const selectedMetricIds = qbSettings.rates?.map((r) => r.metricId?.toString()).filter((id) => id !== undefined && id !== '0');

    const metricItemRender = (li: React.ReactElement, itemProps: any) => {
      const item = itemProps.dataItem;
      const isSelectedElsewhere = selectedMetricIds?.includes(item.id.toString());

      const isCurrentValue = itemProps.value?.toString() === item.id.toString();

      const isDisabled = isSelectedElsewhere && !isCurrentValue;

      return React.cloneElement(li, {
        ...li.props,
        className: `${li.props.className} ${isDisabled ? 'k-disabled' : ''}`,
        onClick: (e: any) => {
          if (isDisabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          if (li.props.onClick) li.props.onClick(e);
        },
      });
    };

    return (
      <div className={styles.qbSettingsInfo}>
        <div className={styles.qbCustomersDropdownWrapper}>
          {quickBookCustomers && quickBookCustomers.length > 0 && (
            <div className={styles.qbCustomersDropdown}>
              {edit ? (
                <Field
                  required={true}
                  name='externalBillingCustomerId'
                  label='Quickbook Customer'
                  validator={requiredValidator}
                  component={FormDropDownWithSearch}
                  data={quickBookCustomers}
                  textField='fullName'
                  value={qbSettings.externalBillingCustomerId}
                  dataItemKey='id'
                  valueMap={(item: any) => item?.id}
                  onChange={(e) => {
                    setQbSettings({
                      ...qbSettings,
                      externalBillingCustomerId: e.value,
                    });
                  }}
                />
              ) : (
                <div className={styles.viewTextFieldWrapper}>
                  <div className={styles.viewTextFieldLabel}>Quickbook Customer</div>
                  <div className={styles.viewTextField}>{selectedCustomer?.fullName || 'N/A'}</div>
                </div>
              )}
            </div>
          )}
          {billingTerms && billingTerms.length > 0 && (
              <div className={styles.qbCustomersDropdown}>
                {edit ? (
                    <Field
                        required={true}
                        name='externalBillingSalesTermId'
                        label='Terms'
                        validator={requiredValidator}
                        component={FormDropDown}
                        data={billingTerms}
                        textField='name'
                        value={qbSettings.externalBillingSalesTermId}
                        dataItemKey='id'
                        valueMap={(item: any) => item?.id}
                        onChange={(e) => {
                          setQbSettings({
                            ...qbSettings,
                            externalBillingSalesTermId: e.value,
                          });
                        }}
                    />
                ) : (
                    <div className={styles.viewTextFieldWrapper}>
                      <div className={styles.viewTextFieldLabel}>Terms</div>
                      <div className={styles.viewTextField}>{selectedSalesTerm?.name || 'N/A'}</div>
                    </div>
                )}
              </div>
          )}
          {billingClasses && billingClasses.length > 0 && (
            <div className={styles.qbCustomersDropdown}>
              {edit ? (
                <Field
                  name='externalBillingClassId'
                  label='Class'
                  component={FormDropDown}
                  data={billingClasses}
                  textField='name'
                  value={qbSettings.externalBillingClassId}
                  dataItemKey='id'
                  valueMap={(item: any) => item?.id}
                  onChange={(e) => {
                    setQbSettings({
                      ...qbSettings,
                      externalBillingClassId: e.value,
                    });
                  }}
                />
              ) : (
                <div className={styles.viewTextFieldWrapper}>
                  <div className={styles.viewTextFieldLabel}>Class</div>
                  <div className={styles.viewTextField}>{selectedClass?.name || 'N/A'}</div>
                </div>
              )}
            </div>
          )}
          {billingDepartments && billingDepartments.length > 0 && (
            <div className={styles.qbCustomersDropdown}>
              {edit ? (
                <Field
                  name='externalBillingDepartmentId'
                  label='Department'
                  component={FormDropDown}
                  data={billingDepartments}
                  textField='name'
                  value={qbSettings.externalBillingDepartmentId}
                  dataItemKey='id'
                  valueMap={(item: any) => item?.id}
                  onChange={(e) => {
                    setQbSettings({
                      ...qbSettings,
                      externalBillingDepartmentId: e.value,
                    });
                  }}
                />
              ) : (
                <div className={styles.viewTextFieldWrapper}>
                  <div className={styles.viewTextFieldLabel}>Department</div>
                  <div className={styles.viewTextField}>{selectedDepartment?.name || 'N/A'}</div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={styles.ratesTableContainer}>
          <Label className={styles.ratesTitle}>Rates</Label>
          <table className={styles.ratesTable}>
            <thead>
              <tr>
                <th style={{ width: '30%' }}>Metric</th>
                <th style={{ width: '30%' }}>Quickbooks SKU</th>
                <th style={{ width: '35%' }}>Customer Rate</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {qbSettings.rates?.map((rate, index) => {
                const selectedMetric = billingMetrics.find((m) => m.id == rate.metricId?.toString());
                const selectedProduct = productsSKU.find((p) => p.id === rate.externalBillingSKUId);
                const currentRateValue = rate.rate !== undefined ? rate.rate : selectedProduct?.unitPrice;

                return (
                  <tr key={index}>
                    <td>
                      {edit ? (
                        <Field className={styles.customErrorWrapper}
                          name={`rates[${index}].metricId`}
                          validator={requiredValidator}
                          component={FormDropDown}
                          data={billingMetrics}
                          textField='name'
                          dataItemKey='id'
                          itemRender={metricItemRender}
                          value={rate.metricId}
                          valueMap={(item: any) => item?.id}
                          onChange={(e) => {
                            const newRates = [...qbSettings.rates];
                            newRates[index].metricId = e.value;
                            setQbSettings({ ...qbSettings, rates: newRates });
                          }}
                        />
                      ) : (
                        <div className={styles.viewTextField}>{selectedMetric?.name || 'N/A'}</div>
                      )}
                    </td>
                    <td>
                      {edit ? (
                        <Field className={styles.customErrorWrapper}
                          name={`rates[${index}].externalBillingSKUId`}
                          validator={requiredValidator}
                          component={FormDropDownWithSearch}
                          data={productsSKU}
                          value={rate.externalBillingSKUId}
                          textField='name'
                          dataItemKey='id'
                          valueMap={(item: any) => item?.id}
                          onChange={(e) => {
                            const newSkuId = e.value;
                            const product = productsSKU.find((p) => p.id === newSkuId);
                            const newPrice = product?.unitPrice;

                            const newRates = [...qbSettings.rates];
                            newRates[index].externalBillingSKUId = newSkuId;
                            newRates[index].rate = newPrice;
                            setQbSettings({ ...qbSettings, rates: newRates });

                            onFormChange(`rates[${index}].externalBillingSKUId`, { value: newSkuId });
                            onFormChange(`rates[${index}].rate`, { value: newPrice });
                          }}
                        />
                      ) : (
                        <div className={styles.viewTextField}>{selectedProduct?.name || 'N/A'}</div>
                      )}
                    </td>
                    <td>
                      {edit ? (
                        <Field
                          key={`rate-field-${index}-${rate.externalBillingSKUId}`}
                          name={`rates[${index}].rate`}
                          component={NumericTextBox}
                          format='c2'
                          spinners={false}
                          value={currentRateValue ?? null}
                          onChange={(e) => {
                            const val = e.value === null ? undefined : e.value;
                            const newRates = [...qbSettings.rates];
                            newRates[index].rate = val;
                            setQbSettings({ ...qbSettings, rates: newRates });
                            onFormChange(`rates[${index}].rate`, { value: val });
                          }}
                          className='k-textbox'
                        />
                      ) : (
                        <div className={styles.viewTextField}>
                          {currentRateValue !== undefined
                            ? currentRateValue.toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                            : '—'}
                        </div>
                      )}
                    </td>
                    <td className={styles.actionBtn}>
                      {edit && (
                        <Button
                          disabled={qbSettings.rates.length <= 1}
                          className={'action-grid-button'}
                          svgIcon={trashIcon}
                          onClick={() => handleRemoveMetric(index, onFormChange)}
                          type='button'
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {edit && (
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ paddingTop: '12px' }}>
                    <Button
                      disabled={qbSettings.rates.length >= billingMetrics.length}
                      fillMode='flat'
                      themeColor='primary'
                      onClick={() => handleAddMetric()}
                      type='button'
                    >
                      + Add Metric
                    </Button>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <Field
          key={`attach-${billingMode}-${qbSettings.invoiceAttachmentsEnabled}`}
          name={'invoiceAttachmentsEnabled'}
          label={'Generate and Include Attachment into Invoice'}
          edit={edit}
          secondLabel={'Enabled'}
          value={qbSettings.invoiceAttachmentsEnabled}
          onChange={(e) => {
            setQbSettings({ ...qbSettings, invoiceAttachmentsEnabled: e.value });
          }}
          component={CheckBoxFormField}
        />
        {billingMode === BillingMode.NexPark && (
          <Field
            key={`breakdown-${billingMode}-${qbSettings.breakdownByLotOwner}`}
            name={'breakdownByLotOwner'}
            label={'Breakdown by Lot Owner'}
            edit={edit}
            secondLabel={'Enabled'}
            value={qbSettings.breakdownByLotOwner}
            onChange={(e) => {
              setQbSettings({ ...qbSettings, breakdownByLotOwner: e.value });
            }}
            component={CheckBoxFormField}
          />
        )}
      </div>
    );
  };
  if (loadingState.loading) return <Spinner />;

  return (
    <Form
      key={`form-${customerId}-${billingMode}-${edit}-${qbSettings.integrationEnabled}`}
      initialValues={qbSettings}
      render={(formRenderProps) => {
        formRef.current = formRenderProps;
        return (
          <FormElement>
            {loadingState.loading && (
              <div style={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner />
              </div>
            )}

            {hasBillingAccess && !loadingState.loading && (
              <div className={styles.settingsActionBtns}>
                {edit ? (
                  <>
                    <Button disabled={loadingState.loading} onClick={(event) => handelSettingsCancel(event)} type='button'>
                      Cancel
                    </Button>
                    <Button
                      disabled={loadingState.loading}
                      onClick={() => onSaveClick(formRenderProps)}
                      type='button'
                      themeColor={'primary'}
                    >
                      Save
                    </Button>
                  </>
                ) : (
                  hasEditAccess && (
                    <Button disabled={loadingState.loading} onClick={() => setEdit(true)}>
                      Edit
                    </Button>
                  )
                )}
              </div>
            )}

            {loadingState.error && <div className={styles.errorLoadSettings}>{loadingState.error}</div>}

            {!loadingState.loading && (
              <>
                <div className={styles.settingsSwitchContainer}>
                  <Switch
                    disabled={!edit}
                    checked={qbSettings.integrationEnabled}
                    onChange={(event: SwitchChangeEvent) => handleServiceToggle(event.value, formRenderProps.onChange)}
                  />
                  <Label>QuickBooks Integration</Label>
                </div>

                {qbSettings.integrationEnabled && !loadingState.error && renderSettingsFields(formRenderProps.onChange)}
              </>
            )}
          </FormElement>
        );
      }}
    />
  );
};
